import { FC, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Col, Row } from 'react-bootstrap';
import { useNavigationData } from '../modules/NavigationProvider';
import { usePolesContext } from '../modules/poles/PolesContext';
import { useEquipmentContext } from '../modules/equipment/EquipmentContext';
import ToggleSwitch from '../components/ToggleSwitch';
import IconSVG from '../components/IconSVG';
import { Firmware } from '../modules/poles/_models';
import { Equipment } from '../modules/equipment/_models';

const MenuPage: FC = () => {
  const { t } = useTranslation('menu');
  const { setPath, setButtonText, setHeaderText } = useNavigationData();
  const { currentPole, onChangeAlarms } = usePolesContext();
  const { equipment } = useEquipmentContext();
  const [alarmSwitch, setAlarmSwitch] = useState<boolean>(false);
  const timerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    setPath('/map');
    setButtonText();
    setHeaderText(currentPole?.name ?? 'VihuPole');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!currentPole?.firmware) return;

    const keys = Object.keys(currentPole.firmware).filter((k) =>
      k.startsWith('in')
    ) as (keyof Firmware)[];

    if (!keys.length) return;

    const items = keys
      .filter((key) => 'name' in currentPole.firmware[key])
      .map((key) => currentPole.firmware[key]);

    if (!items.length) return;

    const active = equipment
      .filter((e) => e.type === 'alarms')
      .some(
        (e: Equipment) =>
          items.findIndex(
            (i) => 'name' in i && 'alert' in i && i.name === e.name && i.alert
          ) !== -1
      );

    setAlarmSwitch(active);
  }, [currentPole?.firmware, equipment]);

  const handleAlarmSwitch = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    setAlarmSwitch(!alarmSwitch);

    timerRef.current = window.setTimeout(() => {
      onChangeAlarms(!alarmSwitch);
    }, 1000);
  };

  return (
    <>
      <div className="d-flex justify-content-center align-items-center">
        <div className="d-flex flex-column">
          <ToggleSwitch checked={alarmSwitch} onChange={handleAlarmSwitch} />
          <span className="text-uppercase text-center">
            {t(alarmSwitch ? 'alarm.off' : 'alarm.on')}
          </span>
        </div>
      </div>

      {/* Pole Section */}
      <div className="pole-bg">
        <Row xs={2} className="h-100 mt-5 text-center">
          <Col className="align-self-center">
            <Link to="/equipments">
              <Button className="btn-circle p-2">
                <IconSVG name="equipments" />
              </Button>
              <p className="text-uppercase">{t('equipments')}</p>
            </Link>
          </Col>
          {(currentPole?.firmware.ledrgb.allowed ||
            currentPole?.firmware.ledw.allowed) && (
            <Col className="align-self-center">
              <Link to="/lights">
                <Button className="btn-circle p-2">
                  <IconSVG name="light" />
                </Button>
                <p className="text-uppercase">{t('lights')}</p>
              </Link>
            </Col>
          )}
          <Col className="align-self-center">
            <Link to="/alerts">
              <Button className="btn-circle p-2">
                <IconSVG name="alert" />
              </Button>
              <p className="text-uppercase">{t('alerts')}</p>
            </Link>
          </Col>
          <Col className="align-self-center">
            <Link to="/energy">
              <Button className="btn-circle p-2">
                <IconSVG name="energy" />
              </Button>
              <p className="text-uppercase">{t('energy')}</p>
            </Link>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default MenuPage;
